import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTOSAVE_DEBOUNCE_MS = 1500;
const AUTOSAVE_MIN_CHARS = 3;

/**
 * Autosaves the current sermon draft (topic + notes + outline + research)
 * to `/api/sermons/drafts`:
 *  - Creates a new draft on first meaningful edit (topic ≥ 3 chars).
 *  - PUTs updates on subsequent edits, debounced AUTOSAVE_DEBOUNCE_MS.
 *  - Exposes `status` ∈ {'idle' | 'saving' | 'saved' | 'error'} for the UI.
 *
 * The hook is intentionally decoupled from the SermonPrepTool component's
 * internal state; the caller passes its current values each render and the
 * hook tracks change-detection internally.
 */
export default function useSermonAutosave({
  topic,
  notes,
  outline,
  research,
  draftId,
  setDraftId,
  seriesId = null,
  seriesPosition = null,
  tags = [],
}) {
  const [status, setStatus] = useState('idle');   // 'idle' | 'saving' | 'saved' | 'error'
  const [savedAt, setSavedAt] = useState(null);   // epoch ms
  const timerRef = useRef(null);
  const lastPayloadRef = useRef('');
  const inflightRef = useRef(false);

  const buildPayload = useCallback(() => ({
    topic: topic || '',
    notes: notes || '',
    outline: (outline || []).map((p) => ({
      id: p.id,
      title: p.title || '',
      content: p.content || '',
    })),
    research: research ?? null,
    series_id: seriesId || null,
    series_position: seriesPosition ?? null,
    tags: Array.isArray(tags) ? tags : [],
  }), [topic, notes, outline, research, seriesId, seriesPosition, tags]);

  const save = useCallback(async () => {
    if (inflightRef.current) return;
    const payload = buildPayload();
    const serialized = JSON.stringify(payload);
    if (serialized === lastPayloadRef.current) return;  // nothing changed
    if ((payload.topic || '').trim().length < AUTOSAVE_MIN_CHARS) return;
    inflightRef.current = true;
    setStatus('saving');
    try {
      let id = draftId;
      if (!id) {
        const res = await axios.post(`${API}/api/sermons/drafts`, payload, {
          withCredentials: true,
        });
        id = res.data.draft_id;
        setDraftId(id);
      } else {
        await axios.put(`${API}/api/sermons/drafts/${id}`, payload, {
          withCredentials: true,
        });
      }
      lastPayloadRef.current = serialized;
      setSavedAt(Date.now());
      setStatus('saved');
    } catch (err) {
      console.warn('Sermon autosave failed:', err);
      setStatus('error');
    } finally {
      inflightRef.current = false;
    }
  }, [buildPayload, draftId, setDraftId]);

  // Debounce on every change.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Skip scheduling if topic is too short and we don't already have a draft.
    if (!draftId && (topic || '').trim().length < AUTOSAVE_MIN_CHARS) return;
    timerRef.current = setTimeout(save, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, notes, outline, research, draftId, seriesId, seriesPosition, tags]);

  // Best-effort flush when the tab is hidden/unmounted.
  useEffect(() => {
    const handler = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      save();
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      handler();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    return save();
  }, [save]);

  const resetStatus = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    lastPayloadRef.current = '';
    setSavedAt(null);
    setStatus('idle');
  }, []);

  return { status, savedAt, saveNow, resetStatus };
}
